// src/components/UserMeet/User-Meet-Private/CreateBreakoutModal.jsx
import React, { useState } from "react";
import { X, Users, Clock, Info } from "lucide-react";

export default function CreateBreakoutModal({
  open,
  onClose,
  onCreate,
  creating,
}) {
  const [timeLimitMinutes, setTimeLimitMinutes] = useState(15);

  const handleTimeChange = (value) => {
    if (value === "") {
      setTimeLimitMinutes("");
      return;
    }

    const parsed = parseInt(value, 10);

    if (!Number.isNaN(parsed)) {
      setTimeLimitMinutes(Math.max(1, Math.min(120, parsed)));
    }
  };

  const adjustTime = (amount) => {
    setTimeLimitMinutes((prev) => {
      const current = Number(prev) || 15;
      return Math.max(1, Math.min(120, current + amount));
    });
  };

  const handleCreate = () => {
    const minutes = Number(timeLimitMinutes);

    if (Number.isNaN(minutes) || minutes < 1 || minutes > 120) {
      return;
    }

    onCreate({ timeLimitMinutes: minutes });
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/75 p-5 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
    >
      <div
        className="flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-green-500/10 bg-gradient-to-br from-slate-900 to-slate-800 shadow-2xl animate-slideUp"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between gap-4 border-b border-white/10 px-6 py-5">
          <div className="flex flex-1 items-start gap-4">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-green-500/30 bg-gradient-to-br from-green-500/15 to-emerald-500/15">
              <Users className="h-6 w-6 text-green-400" />
            </div>

            <div>
              <h2 className="mb-1 text-xl font-bold text-white">
                Create Breakout Room
              </h2>
              <p className="text-sm text-white/60">
                Start a private conversation within this meeting
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            disabled={creating}
            type="button"
            className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg border border-white/10 bg-white/5 text-white/70 transition-all hover:border-red-500/30 hover:bg-red-500/15 hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-5">
          {/* Info Card */}
          <div className="mb-6 flex gap-3 rounded-xl border border-blue-500/20 bg-blue-500/8 p-4">
            <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-blue-400" />

            <div className="flex-1">
              <p className="mb-2 text-sm font-semibold text-blue-400">
                How Breakout Rooms Work
              </p>

              <ul className="list-inside list-disc space-y-1 text-sm text-white/70">
                <li>You'll be moved to a private sub-room</li>
                <li>Invite specific participants to join you</li>
                <li>Return to main meeting anytime</li>
                <li>Room automatically closes when time expires</li>
              </ul>
            </div>
          </div>

          {/* Time Limit Selector */}
          <div className="mb-5">
            <label className="mb-3 flex items-center gap-2 text-sm font-semibold text-white">
              <Clock size={16} className="text-green-400" />
              <span>Time Limit</span>
            </label>

            <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-4">
              <div className="flex items-center justify-center gap-4">
                {/* Decrease */}
                <div className="flex flex-wrap items-center justify-end gap-1.5">
                  {[-30, -15, -10, -5].map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => adjustTime(amount)}
                      disabled={creating || timeLimitMinutes <= 1}
                      className="min-w-[42px] rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs font-semibold text-white/70 transition-all hover:border-red-500/30 hover:bg-red-500/10 hover:text-red-400 disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      {amount}
                    </button>
                  ))}
                </div>

                {/* Center Input */}
                <div className="flex flex-shrink-0 items-center gap-2">
                  <input
                    type="number"
                    min="1"
                    max="120"
                    value={timeLimitMinutes}
                    onChange={(e) => handleTimeChange(e.target.value)}
                    onBlur={() => {
                      if (
                        timeLimitMinutes === "" ||
                        Number.isNaN(Number(timeLimitMinutes))
                      ) {
                        setTimeLimitMinutes(15);
                      }
                    }}
                    disabled={creating}
                    className="w-24 rounded-xl border border-green-500/30 bg-white/5 px-3 py-3 text-center text-2xl font-bold text-white outline-none transition-all focus:border-green-500/70 focus:bg-white/[0.08] focus:ring-2 focus:ring-green-500/10 disabled:cursor-not-allowed disabled:opacity-50 [appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
                    aria-label="Time limit in minutes"
                  />

                  <span className="text-sm font-medium text-white/50">min</span>
                </div>

                {/* Increase */}
                <div className="flex flex-wrap items-center justify-start gap-1.5">
                  {[5, 10, 15, 30].map((amount) => (
                    <button
                      key={amount}
                      type="button"
                      onClick={() => adjustTime(amount)}
                      disabled={creating || timeLimitMinutes >= 120}
                      className="min-w-[42px] rounded-lg border border-white/10 bg-white/5 px-2.5 py-2 text-xs font-semibold text-white/70 transition-all hover:border-green-500/30 hover:bg-green-500/10 hover:text-green-400 disabled:cursor-not-allowed disabled:opacity-30"
                    >
                      +{amount}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-4 text-center">
                <span className="text-xs text-white/40">
                  Minimum 1 minute · Maximum 120 minutes
                </span>
              </div>
            </div>

            <p className="mt-2 text-xs text-white/50">
              Type a custom duration or use the quick adjustment buttons.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex flex-wrap items-center gap-3 border-t border-white/10 px-6 py-5">
          <button
            onClick={onClose}
            disabled={creating}
            type="button"
            className="rounded-xl border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-semibold text-white/80 transition-all hover:border-white/25 hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-50"
          >
            Cancel
          </button>

          <button
            onClick={handleCreate}
            disabled={
              creating ||
              timeLimitMinutes === "" ||
              Number(timeLimitMinutes) < 1 ||
              Number(timeLimitMinutes) > 120
            }
            type="button"
            className="ml-auto flex items-center gap-2 rounded-xl border border-green-500/50 bg-gradient-to-r from-green-500 to-emerald-500 px-5 py-2.5 text-sm font-semibold text-white transition-all hover:-translate-y-0.5 hover:from-emerald-500 hover:to-green-600 hover:shadow-lg hover:shadow-green-500/30 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {creating ? (
              <>
                <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-white/30 border-t-white" />
                Creating...
              </>
            ) : (
              <>
                <Users size={16} />
                Create Breakout Room
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}
